/**
 * @sazeo/sdk — Employee Schedules hooks
 *
 * TanStack Query wrappers for the employee-schedules addon. All calls route
 * through the Edge Function at `/addons/employee-schedules/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScheduleTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleShift {
  id: string;
  tenant_id: string;
  template_id: string;
  /** 0 = Sunday … 6 = Saturday */
  day_of_week: number;
  start_time: string;
  end_time: string;
  label: string | null;
  created_at: string;
}

export interface ScheduleAssignment {
  id: string;
  tenant_id: string;
  user_id: string;
  template_id: string;
  effective_from: string;
  effective_to: string | null;
  created_by: string | null;
  created_at: string;
}

/** Single template with its shifts eagerly joined */
export interface ScheduleTemplateDetail extends ScheduleTemplate {
  shifts: ScheduleShift[];
}

/** Active assignment for a user with the joined template + shifts */
export interface MyScheduleResult {
  assignment: ScheduleAssignment | null;
  template: ScheduleTemplateDetail | null;
}

export interface ScheduleTemplateCreateInput {
  tenant_id: string;
  name: string;
  description?: string | null;
}

export interface ScheduleShiftCreateInput {
  tenant_id: string;
  template_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  label?: string | null;
}

export interface AssignScheduleInput {
  tenant_id: string;
  user_id: string;
  template_id: string;
  effective_from: string;
  effective_to?: string | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const scheduleKeys = {
  all: ['employee-schedules'] as const,
  templates: (tenantId: string) => [...scheduleKeys.all, 'templates', tenantId] as const,
  template: (templateId: string) => [...scheduleKeys.all, 'template', templateId] as const,
  shifts: (templateId: string) => [...scheduleKeys.all, 'shifts', templateId] as const,
  assignments: (tenantId: string) => [...scheduleKeys.all, 'assignments', tenantId] as const,
  mySchedule: (userId: string, tenantId: string) =>
    [...scheduleKeys.all, 'my-schedule', tenantId, userId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers (mirrors contacts.ts pattern)
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** List all templates for a tenant. */
export function useScheduleTemplates(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: scheduleKeys.templates(tenantId),
    queryFn: () =>
      apiFetch<ScheduleTemplate[]>(
        supabase,
        `addons/employee-schedules/templates?tenant_id=${encodeURIComponent(tenantId)}`,
      ),
    enabled: Boolean(tenantId),
  });
}

/** Single template with its shifts. */
export function useScheduleTemplate(supabase: SupabaseClientLike, templateId: string) {
  return useQuery({
    queryKey: scheduleKeys.template(templateId),
    queryFn: () =>
      apiFetch<ScheduleTemplateDetail>(
        supabase,
        `addons/employee-schedules/templates/${templateId}`,
      ),
    enabled: Boolean(templateId),
  });
}

/** Create a new schedule template. */
export function useCreateScheduleTemplate(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ScheduleTemplateCreateInput) =>
      apiFetch<ScheduleTemplate>(supabase, 'addons/employee-schedules/templates', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: scheduleKeys.templates(vars.tenant_id) });
    },
  });
}

/** List shifts for a given template. */
export function useScheduleShifts(supabase: SupabaseClientLike, templateId: string) {
  return useQuery({
    queryKey: scheduleKeys.shifts(templateId),
    queryFn: () =>
      apiFetch<ScheduleShift[]>(
        supabase,
        `addons/employee-schedules/templates/${templateId}/shifts`,
      ),
    enabled: Boolean(templateId),
  });
}

/** Add a shift to a template. */
export function useAddScheduleShift(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ScheduleShiftCreateInput) =>
      apiFetch<ScheduleShift>(
        supabase,
        `addons/employee-schedules/templates/${input.template_id}/shifts`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: scheduleKeys.shifts(vars.template_id) });
      void qc.invalidateQueries({ queryKey: scheduleKeys.template(vars.template_id) });
    },
  });
}

/** Delete a shift from a template. */
export function useDeleteScheduleShift(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shiftId, templateId }: { shiftId: string; templateId: string }) => {
      void templateId;
      return apiFetch<void>(supabase, `addons/employee-schedules/shifts/${shiftId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: scheduleKeys.shifts(vars.templateId) });
      void qc.invalidateQueries({ queryKey: scheduleKeys.template(vars.templateId) });
    },
  });
}

/**
 * Current user's active assignment + the template's shifts.
 * Returns { assignment: null, template: null } when no active schedule exists.
 */
export function useMySchedule(supabase: SupabaseClientLike, userId: string, tenantId: string) {
  return useQuery({
    queryKey: scheduleKeys.mySchedule(userId, tenantId),
    queryFn: () =>
      apiFetch<MyScheduleResult>(
        supabase,
        `addons/employee-schedules/my-schedule?user_id=${encodeURIComponent(userId)}`,
      ),
    enabled: Boolean(userId) && Boolean(tenantId),
  });
}

/** All assignments for a tenant (admin view). */
export function useScheduleAssignments(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: scheduleKeys.assignments(tenantId),
    queryFn: () =>
      apiFetch<ScheduleAssignment[]>(
        supabase,
        `addons/employee-schedules/assignments?tenant_id=${encodeURIComponent(tenantId)}`,
      ),
    enabled: Boolean(tenantId),
  });
}

/**
 * Alias for useScheduleAssignments — all assignments for a tenant (admin view).
 * Exported under the name expected by ScheduleAdminPage.
 */
export const useEmployeeSchedules = useScheduleAssignments;

/** Create or update a schedule assignment for a user. */
export function useAssignSchedule(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignScheduleInput) =>
      apiFetch<ScheduleAssignment>(supabase, 'addons/employee-schedules/assignments', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: scheduleKeys.assignments(vars.tenant_id) });
      void qc.invalidateQueries({
        queryKey: scheduleKeys.mySchedule(vars.user_id, vars.tenant_id),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Shift Swap Requests
// ---------------------------------------------------------------------------

export interface ShiftSwapRequest {
  id: string;
  tenant_id: string;
  from_employee_id: string;
  to_employee_id: string;
  template_id: string;
  shift_date: string;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShiftSwapRequestInput {
  to_employee_id: string;
  template_id: string;
  shift_date: string;
  reason?: string;
}

export const swapRequestKeys = {
  mine: (tenantId: string) => [...scheduleKeys.all, 'swap-requests', 'mine', tenantId] as const,
  tenant: (tenantId: string) => [...scheduleKeys.all, 'swap-requests', 'tenant', tenantId] as const,
} as const;

/** List swap requests — scope='mine' returns only the caller's, 'tenant' returns all for the tenant. */
export function useShiftSwapRequests(
  supabase: SupabaseClientLike,
  tenantId: string,
  options: { scope: 'mine' | 'tenant' },
) {
  const queryKey =
    options.scope === 'mine' ? swapRequestKeys.mine(tenantId) : swapRequestKeys.tenant(tenantId);
  return useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<ShiftSwapRequest[]>(
        supabase,
        `addons/employee-schedules/swap-requests?scope=${options.scope}`,
      ),
    enabled: Boolean(tenantId),
  });
}

/** Submit a new shift-swap request. */
export function useRequestShiftSwap(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShiftSwapRequestInput) =>
      apiFetch<ShiftSwapRequest>(supabase, 'addons/employee-schedules/swap-requests', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: swapRequestKeys.mine(tenantId) });
      void qc.invalidateQueries({ queryKey: swapRequestKeys.tenant(tenantId) });
    },
  });
}

/** Approve a pending shift-swap request by id. */
export function useApproveSwapRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ShiftSwapRequest>(
        supabase,
        `addons/employee-schedules/swap-requests/${id}/approve`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: swapRequestKeys.tenant(tenantId) });
    },
  });
}

/** Reject a pending shift-swap request by id. */
export function useRejectSwapRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ShiftSwapRequest>(supabase, `addons/employee-schedules/swap-requests/${id}/reject`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: swapRequestKeys.tenant(tenantId) });
    },
  });
}
